import { useMutation, useQueryClient } from '@tanstack/react-query'
import { editPresc } from '../../../../apis/editPresc'
import { resetPrescriptionsCache } from '../../../../shared'
import { Prescription } from '../../../../types/models/Presc'
import type { BasicResultFieldKey } from '../fields'
import { REMOTE_INPUT_QUERY_KEY } from '../remoteInput/useRemoteInput'

/** 直せる RP 項目(ADR-0032)。薬品名称・用量は含めない。 */
export type RpFieldKey = '用法名称' | 'コメント'

type Edit =
  | { scope: 'basic'; key: BasicResultFieldKey; value: string }
  | { scope: 'rp'; rpIndex: number; key: RpFieldKey; value: string }

const NO_READ_RESULT = '読み取り結果の無い処方箋(JAHIS QR直読)は編集できません'

/**
 * 直す 1 項目までの経路だけを差し替えた処方箋を返す。**キャッシュ上の実体は書き換えない**。
 *
 * 浅いコピー(`{ ...prescription }`)では basic_result / med_result がキャッシュと同じ参照になり、
 * 送る前の代入がそのまま一覧に効く。backend が編集を拒否(400)しても画面の値は直ったまま残り、
 * 「薬剤師には直ったと見え、レセコンには元の値が行く」状態になる。
 */
export const withBasicResultValue = (
  prescription: Prescription,
  key: BasicResultFieldKey,
  value: string
): Prescription => {
  const basic = prescription.basic_result
  // JAHIS QR直読は basic_result 自体が無く、編集対象の項目UIも描画されないため到達しない。
  if (!basic) throw new Error(NO_READ_RESULT)
  return {
    ...prescription,
    basic_result: {
      ...basic,
      [key]: { ...basic[key], selected_option: value },
    },
  }
}

/** RP 単位の項目版。対象の RP だけを差し替え、ほかの RP は元の参照のまま返す。 */
export const withRpItemValue = (
  prescription: Prescription,
  rpIndex: number,
  key: RpFieldKey,
  value: string
): Prescription => {
  const rpUnits = prescription.med_result
  // JAHIS QR直読は med_result 自体が無く、編集対象の項目UIも描画されないため到達しない。
  if (!rpUnits) throw new Error(NO_READ_RESULT)
  return {
    ...prescription,
    med_result: rpUnits.map((rp, index) =>
      index === rpIndex
        ? { ...rp, [key]: { ...rp[key], selected_option: value } }
        : rp
    ),
  }
}

/**
 * 項目 1 つの保存。送るのは共有 API `editPresc`(POST /prescriptions/)で、
 * 保存 1 回につき backend が新しい版を作り JAHIS CSV と QR を作り直す。
 *
 * **成功でも失敗でも一覧キャッシュをサーバの真値に戻す。** backend は CSV を作れない編集を
 * 400 で丸ごと捨てて版を作らないので、戻さないと画面だけが編集後の値を持つ。
 * 頼まれた紙(遠隔入力)は自店の一覧に無く 5 秒の口のキャッシュに居るので、そちらも取り直す。
 * 失敗は握り潰さず呼び出し側(EditableValue)へ渡す — どの項目が通らなかったかは行の中でしか言えない。
 */
export const useEditField = (prescription: Prescription) => {
  const queryClient = useQueryClient()

  const settle = () =>
    Promise.all([
      resetPrescriptionsCache(queryClient),
      queryClient.invalidateQueries({ queryKey: REMOTE_INPUT_QUERY_KEY }),
    ])

  const mutation = useMutation({
    mutationFn: (edit: Edit) =>
      editPresc(
        edit.key,
        edit.scope === 'basic'
          ? withBasicResultValue(prescription, edit.key, edit.value)
          : withRpItemValue(prescription, edit.rpIndex, edit.key, edit.value)
      ),
    onSuccess: settle,
    onError: settle,
  })

  // 返り値(新しい版)は使わない。画面はキャッシュの戻しから描き直す。
  return {
    editBasicResult: async (key: BasicResultFieldKey, value: string) => {
      await mutation.mutateAsync({ scope: 'basic', key, value })
    },
    editRpItem: async (rpIndex: number, key: RpFieldKey, value: string) => {
      await mutation.mutateAsync({ scope: 'rp', rpIndex, key, value })
    },
  }
}
